'use client';

import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

// Define the types of cookies/services we want to manage consent for
export type ConsentCategory = 'necessary' | 'analytics' | 'external-media';

export interface ConsentState {
  necessary: boolean;
  analytics: boolean;
  externalMedia: boolean;
}

// Define the shape of our context
  interface CookieConsentContextType {
  consent: ConsentState;
  setConsent: (newConsent: ConsentState) => void;
  showConsentBanner: boolean;
  setShowConsentBanner: (show: boolean) => void;
  showConsentManager: boolean;
  setShowConsentManager: (show: boolean) => void;
}

// Create the context with a default value
const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

// Define the props for our provider component
interface CookieConsentProviderProps {
  children: ReactNode;
}

// Local storage key for storing consent
const CONSENT_STORAGE_KEY = 'cookie-consent';

export function CookieConsentProvider({ children }: CookieConsentProviderProps) {
  // Initialize consent state
  const [consent, setConsentState] = useState<ConsentState>({
    necessary: true, // Always true
    analytics: false,
    externalMedia: false,
  });

  // State to control the visibility of the consent banner and manager
  const [showConsentBanner, setShowConsentBanner] = useState(true);
  const [showConsentManager, setShowConsentManager] = useState(false);

  // Load consent from localStorage on initial render
  useEffect(() => {
    const storedConsent = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (storedConsent) {
      try {
        const parsedConsent = JSON.parse(storedConsent);
        setConsentState(parsedConsent);
        setShowConsentBanner(false); // Hide banner if consent is already set
      } catch (error) {
        console.error('Failed to parse stored consent:', error);
      }
    }
  }, []);

  // Update localStorage when consent changes
  const setConsent = (newConsent: ConsentState) => {
    setConsentState(newConsent);
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(newConsent));
  };

  return (
    <CookieConsentContext.Provider
      value={{
        consent,
        setConsent,
        showConsentBanner,
        setShowConsentBanner,
        showConsentManager,
        setShowConsentManager,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}

// Custom hook to use the cookie consent context
export function useCookieConsent() {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
}
